import { Immutable } from "immer";
import React, { useId } from "react";
import { t } from "ttag";

import type { SharedFAQsBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { getGlobalSpacingClasses } from "../_spacing/globalSpacing";

import styles from "./index.module.scss";

type IProps = Immutable<{
    value: Value;
}>;

/**
 * Faithful port of the legacy server-rendered
 * `tsicommon_cms/blocks/faqs-block.html`. The FAQPage JSON-LD schema stays
 * SERVER-side in the Django wrapper (crawlers need it in the initial HTML) — do
 * not move it into React. The global accordion widget (`forms/initWidgets.tsx`)
 * skips triggers inside `[data-include-react]` islands, so it cannot double-bind
 * these React-owned triggers. The `useId()`-derived panel id is a11y-only (the
 * widget never reads it), so differing from the legacy literal is intentional
 * and not pixel-visible. `question`/`answer` arrive already `.rich-text`-wrapped
 * via Wagtail's `richtext()`, so raw `dangerouslySetInnerHTML` is the
 * wrapper-free match (which is why `.rich-text` stays `:global`).
 */
export const FAQsBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const baseId = useId();
    const [openPanels, setOpenPanels] = React.useState<ReadonlySet<number>>(
        new Set(),
    );
    const faqs = v.faqs ?? [];
    return (
        <section
            className={concatClassNames([
                styles.faqsBlock,
                "l-max-width",
                v.background_style?.style ?? "",
                getGlobalSpacingClasses(v.spacing),
            ])}
        >
            <header>
                <RichTextBlock value={v.header_content ?? ""} />
                {v.view_all_link && (
                    <a href={v.view_all_link.url}>{t`View all FAQs`}</a>
                )}
            </header>
            <ul>
                {faqs.map((faq, i) => {
                    const panelId = `${baseId}-${i}`;
                    const isOpen = openPanels.has(i);
                    return (
                        <li key={faq.id}>
                            <h3>
                                <button
                                    className={concatClassNames([
                                        styles.trigger,
                                        "accordion-trigger",
                                        isOpen ? styles.isActive : "",
                                        isOpen ? "--is-active" : "",
                                    ])}
                                    aria-expanded={isOpen ? "true" : "false"}
                                    aria-controls={panelId}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setOpenPanels((value) => {
                                            const next = new Set(value);
                                            if (next.has(i)) {
                                                next.delete(i);
                                            } else {
                                                next.add(i);
                                            }
                                            return next;
                                        });
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: faq.question,
                                    }}
                                />
                            </h3>
                            <div
                                id={panelId}
                                aria-hidden={isOpen ? "false" : "true"}
                                className={concatClassNames([
                                    "accordion-target",
                                    isOpen ? styles.isActive : "",
                                    isOpen ? "accordion-target--is-active" : "",
                                ])}
                                dangerouslySetInnerHTML={{
                                    __html: faq.answer,
                                }}
                            />
                        </li>
                    );
                })}
            </ul>
        </section>
    );
};
